import React, { useEffect, useState } from 'react';
import { Settings, Trash2, X } from 'lucide-react';
import { auth } from '../../config/firebase';
import useAdminStore from '../../store/adminStore';
import useAppStore from '../../store/appStore';
import { usePEInvoiceStore } from '../../store/usePEInvoiceStore';
import { generatePEInvoicePdf, generatePEInvoiceHybridPdf } from '../../utils/pe-papers/peInvoicePdfGenerator';
import { generateZugferdXml } from '../../utils/pe-papers/zugferdGenerator';
import { isValidIban } from '../../utils/pe-papers/ibanValidation';
import { savePePapersArchiveDoc } from '../../services/pePapersArchiveService';
import PEInvoiceSettingsModal from './PEInvoiceSettingsModal';
import PEInvoicePreview from './PEInvoicePreview';

export interface PEInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialData?: any;
  onArchived?: () => void;
}

const inputClass =
  'w-full rounded-lg border border-pe-border dark:border-slate-700 bg-pe-input dark:bg-black text-pe-text dark:text-white px-3 py-2 text-sm placeholder:text-pe-muted dark:placeholder:text-slate-500 focus:outline-none focus:border-[#E6C299]/50 transition-colors';

const labelClass =
  'mb-1 block text-[10px] font-bold uppercase tracking-wider text-pe-muted dark:text-slate-400';

const segmentWrapClass =
  'inline-flex rounded-lg border border-pe-border dark:border-slate-700 bg-pe-input dark:bg-black p-1 gap-1';

const segmentActiveClass =
  'rounded-md px-4 py-2 text-xs font-bold uppercase tracking-widest bg-[#E6C299]/20 text-pe-accent-text dark:text-[#E6C299] border border-[#E6C299]/40';

const segmentIdleClass =
  'rounded-md px-4 py-2 text-xs font-bold uppercase tracking-widest text-pe-muted dark:text-slate-400 hover:text-pe-text dark:hover:text-slate-200 border border-transparent';

const btnSecondaryClass =
  'flex-1 rounded-xl border border-pe-border-strong dark:border-[#444] bg-transparent px-4 py-3 text-xs font-bold uppercase tracking-widest text-pe-muted dark:text-gray-300 hover:bg-pe-hover dark:hover:bg-[#222] transition-colors';

const btnPrimaryClass =
  'flex-1 rounded-xl bg-[#E6C299] hover:bg-[#d9b183] dark:hover:bg-white px-4 py-3 text-xs font-bold uppercase tracking-widest text-black transition-colors';

const panelClass =
  'rounded-xl border border-pe-border dark:border-[#222] bg-pe-elevated dark:bg-[#15171e]/60 p-3 space-y-3';

const UNITS = ['Stk.', 'Std.', 'Minuten', 'Pauschal', 'kg'] as const;

const formatEuro = (value: number) =>
  value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

const lineGross = (
  quantity: number,
  unitPrice: number,
  priceMode: 'gross' | 'net',
  vatRate: number,
  isVatExempt: boolean
) => {
  const line = (quantity || 0) * (unitPrice || 0);
  const rate = isVatExempt ? 0 : vatRate;
  if (rate === 0) return line;
  if (priceMode === 'gross') return line;
  return line * (1 + rate / 100);
};

const PEInvoiceModal: React.FC<PEInvoiceModalProps> = ({
  isOpen,
  onClose,
  initialData,
  onArchived,
}) => {
  const profile = usePEInvoiceStore((s) => s.profile);
  const customer = usePEInvoiceStore((s) => s.customer);
  const items = usePEInvoiceStore((s) => s.items);
  const shipping = usePEInvoiceStore((s) => s.shipping);
  const meta = usePEInvoiceStore((s) => s.meta);
  const isLoading = usePEInvoiceStore((s) => s.isLoading);
  const loadProfile = usePEInvoiceStore((s) => s.loadProfile);
  const setCustomerType = usePEInvoiceStore((s) => s.setCustomerType);
  const updateCustomer = usePEInvoiceStore((s) => s.updateCustomer);
  const addItem = usePEInvoiceStore((s) => s.addItem);
  const updateItem = usePEInvoiceStore((s) => s.updateItem);
  const removeItem = usePEInvoiceStore((s) => s.removeItem);
  const updateShipping = usePEInvoiceStore((s) => s.updateShipping);
  const updateMeta = usePEInvoiceStore((s) => s.updateMeta);
  const setDocType = usePEInvoiceStore((s) => s.setDocType);
  const getCalculations = usePEInvoiceStore((s) => s.getCalculations);
  const syncBarometerRates = usePEInvoiceStore((s) => s.syncBarometerRates);
  const loadInvoiceDraft = usePEInvoiceStore((s) => s.loadInvoiceDraft);
  const resetInvoice = usePEInvoiceStore((s) => s.resetInvoice);

  const barometerStundenfaktor = useAppStore(
    (s) => Number(s.clientData?.kalkulator?.barometerStundenfaktor ?? 0)
  );
  const barometerMatPreis = useAppStore((s) => {
    const list = s.clientData?.kalkulator?.priceParameterListe;
    if (!Array.isArray(list)) return 0;
    const matParam =
      list.find((p: any) => {
        const desc = String(p?.desc || '').toLowerCase();
        return desc.includes('stundensatz') || desc.includes('verrechnungssatz');
      }) || list.find((p: any) => p?.id === 'p4');
    return Number(matParam?.preis ?? 0);
  });
  const salonName = useAppStore(
    (s) => s.clientData?.salonname || s.clientData?.firmenname || 'Salon'
  );

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState<'config' | 'preview'>('config');
  const [bankHint, setBankHint] = useState(false);
  const [archiveDocId, setArchiveDocId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [archiveFeedback, setArchiveFeedback] = useState<'idle' | 'success' | 'error' | 'draft'>('idle');

  const hasIban = isValidIban(profile.iban || '');

  useEffect(() => {
    if (!isOpen) return;
    setBankHint(false);
    setArchiveFeedback('idle');
    setIsSaving(false);
    setArchiveDocId(initialData?.id || null);
    setCurrentStep(initialData?.invoiceStep === 'preview' ? 'preview' : 'config');

    const mandantId =
      useAdminStore.getState().impersonatedClientId || auth.currentUser?.uid || '';

    void (async () => {
      if (mandantId) {
        await loadProfile(mandantId);
      }
      if (initialData) {
        loadInvoiceDraft({
          customer: initialData.customer,
          items: initialData.items,
          shipping: initialData.shipping,
          meta: initialData.meta,
        });
      } else {
        resetInvoice();
      }
    })();
  }, [isOpen, initialData, loadProfile, loadInvoiceDraft, resetInvoice]);

  // Barometer-Werte aus Kalkulator nachziehen (auch wenn clientData erst später geladen ist)
  useEffect(() => {
    if (!isOpen) return;
    if (barometerStundenfaktor > 0 || barometerMatPreis > 0) {
      syncBarometerRates();
    }
  }, [isOpen, barometerStundenfaktor, barometerMatPreis, syncBarometerRates]);

  // Sicherheitsnetz: Vorschau nie ohne IBAN
  useEffect(() => {
    if (!isOpen) return;
    if (currentStep === 'preview' && !hasIban) {
      setCurrentStep('config');
      setBankHint(true);
      setIsSettingsOpen(true);
    }
  }, [isOpen, currentStep, hasIban]);

  if (!isOpen) return null;

  const calc = getCalculations();
  const isVatExempt = calc.isVatExempt;
  const standardVatRate = calc.standardVatRate;
  const isEstimate = meta.docType === 'estimate';

  const openBankSettings = () => {
    setBankHint(true);
    setIsSettingsOpen(true);
  };

  const goToPreview = () => {
    if (!hasIban) {
      openBankSettings();
      return;
    }
    setCurrentStep('preview');
  };

  const customerLabel = () => {
    if (customer.type === 'business') {
      return String(customer.companyName || customer.contactPerson || '').trim() || 'Geschäftskunde';
    }
    return `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || 'Privatkunde';
  };

  const handleArchive = async (asDraft = false) => {
    if (isSaving) return;
    setIsSaving(true);
    setArchiveFeedback('idle');
    try {
      const id = await savePePapersArchiveDoc({
        id: archiveDocId,
        documentType: 'pe-invoice',
        status: asDraft ? 'entwurf' : 'final',
        salonName: String(salonName || 'Salon'),
        mitarbeiterName: customerLabel(),
        confirmedDisclaimer: !asDraft,
        payload: {
          customer,
          items,
          shipping,
          meta,
          invoiceStep: currentStep,
        },
      });
      setArchiveDocId(id);
      setArchiveFeedback(asDraft ? 'draft' : 'success');
      onArchived?.();
      if (asDraft) {
        setTimeout(() => onClose(), 600);
      } else {
        setTimeout(() => setArchiveFeedback('idle'), 3000);
      }
    } catch (e) {
      console.error('Rechnung Archivierung fehlgeschlagen:', e);
      setArchiveFeedback('error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!hasIban) {
      openBankSettings();
      return;
    }
    const calculations = getCalculations();
    await generatePEInvoicePdf({
      profile,
      customer,
      items,
      shipping,
      meta,
      calculations,
    });
  };

  const handleDownloadHybridPdf = async () => {
    if (!hasIban) {
      openBankSettings();
      return;
    }
    const calculations = getCalculations();
    await generatePEInvoiceHybridPdf({
      profile,
      customer,
      items,
      shipping,
      meta,
      calculations,
    });
  };

  const handleDownloadZugferdXml = () => {
    const calculations = getCalculations();
    const xmlContent = generateZugferdXml({
      profile,
      customer,
      items,
      shipping,
      meta,
      calculations,
    });
    const blob = new Blob([xmlContent], { type: 'application/xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ZUGFeRD_factur-x_${meta.invoiceNumber || 'Rechnung'}.xml`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const showZugferdDownload =
    currentStep === 'preview' && meta.docType === 'invoice' && customer.type === 'business';

  return (
    <>
      <div
        className="fixed inset-0 bg-stone-900/70 dark:bg-black/90 z-[100000] backdrop-blur-md"
      />
      <div className="fixed inset-x-0 top-[8.5rem] md:top-36 bottom-0 z-[100001] flex items-start justify-center overflow-hidden p-4 pb-8 font-sans pointer-events-none">
        <div
          className="relative my-2 flex max-h-[calc(100vh-9.5rem)] md:max-h-[calc(100vh-10rem)] w-full max-w-6xl min-h-0 flex-col overflow-hidden rounded-2xl border border-[#E6C299]/30 bg-pe-card dark:bg-[#111] text-pe-text dark:text-slate-50 shadow-[0_0_40px_rgba(230,194,153,0.1)] pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex shrink-0 items-start justify-between gap-4 border-b border-pe-border dark:border-[#222] bg-pe-elevated dark:bg-[#111622] px-6 py-5">
            <div>
              <h2 className="text-xl font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]">
                Rechnung / Kostenvoranschlag
              </h2>
              {isLoading ? (
                <p className="mt-1 text-xs text-pe-muted dark:text-slate-500">
                  Profil wird geladen…
                </p>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setIsSettingsOpen(true)}
                className="inline-flex items-center gap-2 rounded-xl border border-[#E6C299]/40 bg-pe-card dark:bg-[#15171e] px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299] hover:bg-pe-hover dark:hover:bg-[#1a1d24] transition-colors"
              >
                <Settings className="h-4 w-4" />
                Salon-Bankdaten & Einstellungen
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-pe-border-strong dark:border-[#333] bg-pe-elevated dark:bg-[#1a1a1a] p-2 text-pe-muted dark:text-slate-400 hover:text-pe-text dark:hover:text-white hover:bg-pe-hover dark:hover:bg-[#222] transition-colors"
                aria-label="Schließen"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
            {!hasIban ? (
              <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-pe-text dark:text-slate-200">
                <p className="font-semibold text-pe-accent-text dark:text-[#E6C299]">
                  IBAN erforderlich
                </p>
                <p className="mt-1 text-xs text-pe-muted dark:text-slate-400">
                  Ohne gültige IBAN kann die Vorschau nicht geöffnet werden. Bitte einmalig
                  hinterlegen (Firebase) — später jederzeit änderbar.
                </p>
                <button
                  type="button"
                  onClick={openBankSettings}
                  className="mt-3 rounded-lg border border-[#E6C299]/40 bg-pe-card dark:bg-[#15171e] px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299] hover:bg-pe-hover dark:hover:bg-[#1a1d24]"
                >
                  IBAN jetzt hinterlegen
                </button>
              </div>
            ) : null}

            {currentStep === 'preview' && hasIban ? (
              <PEInvoicePreview />
            ) : (
              <>
            {/* Dokumententyp */}
            <section className="flex flex-wrap items-center gap-3">
              <div className={segmentWrapClass}>
                <button
                  type="button"
                  onClick={() => setDocType('invoice')}
                  className={!isEstimate ? segmentActiveClass : segmentIdleClass}
                >
                  📄 Rechnung
                </button>
                <button
                  type="button"
                  onClick={() => setDocType('estimate')}
                  className={isEstimate ? segmentActiveClass : segmentIdleClass}
                >
                  📋 Kostenvoranschlag
                </button>
              </div>
            </section>

            {/* Meta */}
            <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <label className="block">
                <span className={labelClass}>
                  {isEstimate ? 'KVA-Nr. / Angebots-Nr.' : 'Rechnungs-Nr.'}
                </span>
                <input
                  className={inputClass}
                  value={meta.invoiceNumber}
                  onChange={(e) => updateMeta({ invoiceNumber: e.target.value })}
                />
              </label>
              <label className="block">
                <span className={labelClass}>
                  {isEstimate ? 'Erstellungsdatum' : 'Rechnungsdatum'}
                </span>
                <input
                  type="date"
                  className={inputClass}
                  value={meta.invoiceDate}
                  onChange={(e) => updateMeta({ invoiceDate: e.target.value })}
                />
              </label>
              <label className="block">
                <span className={labelClass}>
                  {isEstimate ? 'Gültig bis' : 'Leistungsdatum'}
                </span>
                <input
                  type="date"
                  className={inputClass}
                  value={meta.deliveryDate}
                  onChange={(e) => updateMeta({ deliveryDate: e.target.value })}
                />
              </label>
              <label className="block">
                <span className={labelClass}>
                  {isEstimate ? 'Bindefrist (Tage)' : 'Zahlungsziel (Tage)'}
                </span>
                <input
                  type="number"
                  min={0}
                  className={inputClass}
                  value={meta.paymentDeadlineDays}
                  onChange={(e) =>
                    updateMeta({ paymentDeadlineDays: Number(e.target.value) || 0 })
                  }
                />
              </label>
            </section>

            {/* Kundentyp */}
            <section className="space-y-4">
              <div className={segmentWrapClass}>
                <button
                  type="button"
                  onClick={() => setCustomerType('private')}
                  className={
                    customer.type === 'private' ? segmentActiveClass : segmentIdleClass
                  }
                >
                  Privatkunde
                </button>
                <button
                  type="button"
                  onClick={() => setCustomerType('business')}
                  className={
                    customer.type === 'business' ? segmentActiveClass : segmentIdleClass
                  }
                >
                  Firma / Geschäftskunde
                </button>
              </div>

              {customer.type === 'private' ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="block">
                    <span className={labelClass}>Vorname</span>
                    <input
                      className={inputClass}
                      value={customer.firstName}
                      onChange={(e) => updateCustomer({ firstName: e.target.value })}
                    />
                  </label>
                  <label className="block">
                    <span className={labelClass}>Nachname</span>
                    <input
                      className={inputClass}
                      value={customer.lastName}
                      onChange={(e) => updateCustomer({ lastName: e.target.value })}
                    />
                  </label>
                  <label className="block sm:col-span-2">
                    <span className={labelClass}>Straße & Hausnummer</span>
                    <input
                      className={inputClass}
                      value={customer.street}
                      onChange={(e) => updateCustomer({ street: e.target.value })}
                    />
                  </label>
                  <label className="block">
                    <span className={labelClass}>PLZ</span>
                    <input
                      className={inputClass}
                      value={customer.zip}
                      onChange={(e) => updateCustomer({ zip: e.target.value })}
                    />
                  </label>
                  <label className="block">
                    <span className={labelClass}>Ort</span>
                    <input
                      className={inputClass}
                      value={customer.city}
                      onChange={(e) => updateCustomer({ city: e.target.value })}
                    />
                  </label>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="block">
                    <span className={labelClass}>Firmenname</span>
                    <input
                      className={inputClass}
                      value={customer.companyName}
                      onChange={(e) => updateCustomer({ companyName: e.target.value })}
                    />
                  </label>
                  <label className="block">
                    <span className={labelClass}>Ansprechpartner</span>
                    <input
                      className={inputClass}
                      value={customer.contactPerson}
                      onChange={(e) => updateCustomer({ contactPerson: e.target.value })}
                    />
                  </label>
                  <label className="block sm:col-span-2">
                    <span className={labelClass}>Straße & Hausnummer</span>
                    <input
                      className={inputClass}
                      value={customer.street}
                      onChange={(e) => updateCustomer({ street: e.target.value })}
                    />
                  </label>
                  <label className="block">
                    <span className={labelClass}>PLZ</span>
                    <input
                      className={inputClass}
                      value={customer.zip}
                      onChange={(e) => updateCustomer({ zip: e.target.value })}
                    />
                  </label>
                  <label className="block">
                    <span className={labelClass}>Ort</span>
                    <input
                      className={inputClass}
                      value={customer.city}
                      onChange={(e) => updateCustomer({ city: e.target.value })}
                    />
                  </label>
                  <label className="block sm:col-span-2">
                    <span className={labelClass}>Rechnungs-E-Mail (E-Rechnung)</span>
                    <input
                      type="email"
                      className={inputClass}
                      value={customer.email}
                      onChange={(e) => updateCustomer({ email: e.target.value })}
                    />
                  </label>
                  <label className="block sm:col-span-2">
                    <span className={labelClass}>Bestell- / Kundenreferenz (BT-10)</span>
                    <input
                      className={inputClass}
                      value={customer.buyerReference}
                      onChange={(e) => updateCustomer({ buyerReference: e.target.value })}
                      placeholder="Optional — z. B. Bestellnummer"
                    />
                  </label>
                  <label className="block">
                    <span className={labelClass}>Land</span>
                    <input
                      className={inputClass}
                      value={customer.country}
                      onChange={(e) => updateCustomer({ country: e.target.value.toUpperCase() })}
                      placeholder="DE"
                    />
                  </label>
                </div>
              )}
            </section>

            {/* Positionen */}
            <section className="space-y-3">
              <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-pe-accent-text dark:text-[#E6C299]/80 border-b border-pe-border dark:border-[#222] pb-2">
                Positionen
              </h3>
              <div className="space-y-3">
                {items.map((item, idx) => (
                  <div key={item.id} className={panelClass}>
                    <div className="flex flex-wrap items-center gap-2 pl-11">
                      <div className={segmentWrapClass}>
                        <button
                          type="button"
                          onClick={() => updateItem(item.id, { kind: 'service' })}
                          className={
                            item.kind === 'service' ? segmentActiveClass : segmentIdleClass
                          }
                        >
                          Dienstleistung
                        </button>
                        <button
                          type="button"
                          onClick={() => updateItem(item.id, { kind: 'sale' })}
                          className={
                            item.kind === 'sale' ? segmentActiveClass : segmentIdleClass
                          }
                        >
                          Verkauf
                        </button>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <span className="mt-2 w-8 shrink-0 text-center text-xs font-bold text-pe-muted dark:text-slate-500">
                        {idx + 1}
                      </span>
                      <label className="block min-w-0 flex-1">
                        <span className={labelClass}>
                          {item.kind === 'service'
                            ? 'Beschreibung / Leistung'
                            : 'Beschreibung / Artikel'}
                        </span>
                        <textarea
                          rows={3}
                          className={`${inputClass} min-h-[4.5rem] resize-y`}
                          placeholder={
                            item.kind === 'service'
                              ? 'Leistung / Dienstleistung detailliert beschreiben…'
                              : 'Verkaufsartikel beschreiben…'
                          }
                          value={item.description}
                          onChange={(e) =>
                            updateItem(item.id, { description: e.target.value })
                          }
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => removeItem(item.id)}
                        disabled={items.length <= 1}
                        className="mt-6 shrink-0 rounded-lg border border-pe-border-strong dark:border-[#333] p-2 text-rose-400 hover:bg-rose-500/10 disabled:opacity-30 transition-colors"
                        title="Position löschen"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>

                    {item.kind === 'service' ? (
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 pl-11">
                        <label className="block">
                          <span className={labelClass}>Zeitaufwand (Std.)</span>
                          <input
                            type="number"
                            min={0}
                            step="0.25"
                            className={inputClass}
                            value={item.serviceHours}
                            onChange={(e) =>
                              updateItem(item.id, {
                                serviceHours: Number(e.target.value) || 0,
                              })
                            }
                          />
                        </label>
                        <label className="block">
                          <span className={labelClass}>
                            Stundensatz (€)
                            <span className="ml-1 font-normal normal-case tracking-normal text-pe-muted">
                              ·{' '}
                              {item.materialMode === 'inclusive'
                                ? 'Barometer inkl. Material'
                                : 'Barometer ohne Material'}
                              , anpassbar
                            </span>
                          </span>
                          <input
                            type="number"
                            min={0}
                            step="0.01"
                            className={inputClass}
                            value={item.hourlyRate}
                            onChange={(e) =>
                              updateItem(item.id, {
                                hourlyRate: Number(e.target.value) || 0,
                              })
                            }
                          />
                        </label>
                        <div className="block">
                          <span className={labelClass}>Material</span>
                          <div className="inline-flex w-full rounded-lg border border-pe-border dark:border-slate-700 bg-pe-input dark:bg-black p-0.5 gap-0.5">
                            <button
                              type="button"
                              onClick={() =>
                                updateItem(item.id, { materialMode: 'inclusive' })
                              }
                              className={`flex-1 rounded-md px-2 py-2 text-[9px] font-bold uppercase ${
                                item.materialMode === 'inclusive'
                                  ? 'bg-[#E6C299]/20 text-pe-accent-text dark:text-[#E6C299] border border-[#E6C299]/40'
                                  : 'text-pe-muted dark:text-slate-400 border border-transparent'
                              }`}
                            >
                              Inklusive
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                updateItem(item.id, { materialMode: 'exclusive' })
                              }
                              className={`flex-1 rounded-md px-2 py-2 text-[9px] font-bold uppercase ${
                                item.materialMode === 'exclusive'
                                  ? 'bg-[#E6C299]/20 text-pe-accent-text dark:text-[#E6C299] border border-[#E6C299]/40'
                                  : 'text-pe-muted dark:text-slate-400 border border-transparent'
                              }`}
                            >
                              Exklusiv
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : null}

                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6 pl-11">
                      <label className="block">
                        <span className={labelClass}>Menge</span>
                        <input
                          type="number"
                          min={0}
                          step="0.01"
                          className={inputClass}
                          value={item.quantity}
                          onChange={(e) =>
                            updateItem(item.id, {
                              quantity: Number(e.target.value) || 0,
                            })
                          }
                        />
                      </label>
                      <label className="block">
                        <span className={labelClass}>Einheit</span>
                        <select
                          className={inputClass}
                          value={item.unit}
                          onChange={(e) => updateItem(item.id, { unit: e.target.value })}
                        >
                          {UNITS.map((u) => (
                            <option key={u} value={u}>
                              {u}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="block">
                        <span className={labelClass}>Einzelpreis</span>
                        <input
                          type="number"
                          min={0}
                          step="0.01"
                          className={inputClass}
                          value={item.unitPrice}
                          onChange={(e) =>
                            updateItem(item.id, {
                              unitPrice: Number(e.target.value) || 0,
                            })
                          }
                        />
                      </label>
                      <div className="block">
                        <span className={labelClass}>Modus</span>
                        <div className="inline-flex w-full rounded-lg border border-pe-border dark:border-slate-700 bg-pe-input dark:bg-black p-0.5 gap-0.5">
                          <button
                            type="button"
                            onClick={() => updateItem(item.id, { priceMode: 'gross' })}
                            className={`flex-1 rounded-md px-2 py-2 text-[9px] font-bold uppercase ${
                              item.priceMode === 'gross'
                                ? 'bg-[#E6C299]/20 text-pe-accent-text dark:text-[#E6C299] border border-[#E6C299]/40'
                                : 'text-pe-muted dark:text-slate-400 border border-transparent'
                            }`}
                          >
                            Brutto
                          </button>
                          <button
                            type="button"
                            onClick={() => updateItem(item.id, { priceMode: 'net' })}
                            className={`flex-1 rounded-md px-2 py-2 text-[9px] font-bold uppercase ${
                              item.priceMode === 'net'
                                ? 'bg-[#E6C299]/20 text-pe-accent-text dark:text-[#E6C299] border border-[#E6C299]/40'
                                : 'text-pe-muted dark:text-slate-400 border border-transparent'
                            }`}
                          >
                            Netto
                          </button>
                        </div>
                      </div>
                      <div className="block">
                        <span className={labelClass}>MwSt %</span>
                        <div className="rounded-lg border border-pe-border dark:border-slate-700 bg-pe-elevated dark:bg-[#0a0a0a] px-3 py-2 font-mono text-sm text-pe-muted dark:text-slate-300">
                          {isVatExempt ? '0%' : `${standardVatRate}%`}
                        </div>
                      </div>
                      <div className="block">
                        <span className={labelClass}>Gesamt</span>
                        <div className="rounded-lg border border-pe-border dark:border-slate-700 bg-pe-elevated dark:bg-[#0a0a0a] px-3 py-2 font-mono text-sm text-pe-text dark:text-slate-200">
                          {formatEuro(
                            lineGross(
                              item.quantity,
                              item.unitPrice,
                              item.priceMode,
                              standardVatRate,
                              isVatExempt
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={addItem}
                className="rounded-xl border border-dashed border-[#E6C299]/40 px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299] hover:bg-[#E6C299]/10 transition-colors"
              >
                + Position hinzufügen
              </button>
            </section>

            {/* Versand & Notes */}
            <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className={`${panelClass} !space-y-3`}>
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={shipping.enabled}
                    onChange={(e) => updateShipping({ enabled: e.target.checked })}
                    className="h-4 w-4 accent-[#E6C299]"
                  />
                  <span className="text-sm text-pe-text dark:text-slate-200">
                    Versandkosten berechnen
                  </span>
                </label>
                {shipping.enabled ? (
                  <label className="block">
                    <span className={labelClass}>Versandkosten (€)</span>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      className={inputClass}
                      value={shipping.amount}
                      onChange={(e) =>
                        updateShipping({ amount: Number(e.target.value) || 0 })
                      }
                    />
                  </label>
                ) : null}
              </div>
              <label className="block">
                <span className={labelClass}>Anmerkungen / Zahlungshinweise</span>
                <textarea
                  rows={4}
                  className={inputClass}
                  value={meta.notes}
                  onChange={(e) => updateMeta({ notes: e.target.value })}
                />
              </label>
            </section>

            {/* Summen */}
            <section className="rounded-xl border border-slate-300 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/40">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-slate-600 dark:text-slate-300">
                  <span>Zwischensumme Netto</span>
                  <span className="font-mono tabular-nums">{formatEuro(calc.netTotal)}</span>
                </div>
                {isVatExempt ? (
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Umsatzsteuerbefreit (Stammdaten) – keine USt ausgewiesen
                  </p>
                ) : (
                  <div className="flex justify-between text-slate-600 dark:text-slate-400">
                    <span>zzgl. USt {standardVatRate} %</span>
                    <span className="font-mono tabular-nums">
                      {formatEuro(calc.vatBreakdown.standard.vat)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between border-t border-slate-300 pt-3 dark:border-slate-600">
                  <span className="font-bold text-slate-900 dark:text-slate-100">
                    Gesamtsumme Brutto
                  </span>
                  <span className="text-xl font-bold tabular-nums text-slate-900 dark:text-[#E6C299]">
                    {formatEuro(calc.grossTotal)}
                  </span>
                </div>
              </div>
            </section>
              </>
            )}
          </div>

          <div className="flex shrink-0 gap-3 border-t border-pe-border dark:border-[#222] bg-pe-elevated dark:bg-[#111622] px-6 py-4">
            {currentStep === 'preview' ? (
              <>
                <button
                  type="button"
                  onClick={() => setCurrentStep('config')}
                  className={btnSecondaryClass}
                >
                  ← Zurück zur Bearbeitung
                </button>
                <button
                  type="button"
                  onClick={() => void handleArchive(true)}
                  disabled={isSaving}
                  className={`${btnSecondaryClass} disabled:opacity-50`}
                >
                  {archiveFeedback === 'draft' ? 'Gespeichert! ✓' : 'Später fortsetzen'}
                </button>
                <button
                  type="button"
                  onClick={() => void handleArchive(false)}
                  disabled={isSaving}
                  className="flex-1 rounded-xl border border-[#E6C299]/40 bg-pe-card dark:bg-[#15171e] px-4 py-3 text-xs font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299] hover:bg-pe-hover dark:hover:bg-[#1a1d24] transition-colors disabled:opacity-50"
                >
                  {isSaving && archiveFeedback !== 'draft'
                    ? 'Speichert...'
                    : archiveFeedback === 'success'
                      ? 'Gespeichert! ✓'
                      : 'Im Archiv speichern'}
                </button>
                <button
                  type="button"
                  onClick={() => void handleDownloadPdf()}
                  className={btnPrimaryClass}
                >
                  {!hasIban
                    ? 'Bankdaten hinterlegen (für PDF)'
                    : isEstimate
                      ? 'Kostenvoranschlag als PDF herunterladen'
                      : 'Rechnung als PDF herunterladen'}
                </button>
                {showZugferdDownload ? (
                  <>
                    <button
                      type="button"
                      onClick={() => void handleDownloadHybridPdf()}
                      className="rounded-xl bg-emerald-600 hover:bg-emerald-500 px-4 py-3 text-xs font-bold uppercase tracking-widest text-white transition-colors"
                    >
                      E-Rechnung (PDF/A-3 Hybrid) herunterladen
                    </button>
                    <button
                      type="button"
                      onClick={handleDownloadZugferdXml}
                      className="rounded-xl border border-emerald-600/50 bg-transparent hover:bg-emerald-600/10 px-4 py-3 text-xs font-bold uppercase tracking-widest text-emerald-600 dark:text-emerald-400 transition-colors"
                    >
                      ZUGFeRD-XML (Factur-X)
                    </button>
                  </>
                ) : null}
              </>
            ) : (
              <>
                <button type="button" onClick={onClose} className={btnSecondaryClass}>
                  Abbrechen
                </button>
                <button
                  type="button"
                  onClick={() => void handleArchive(true)}
                  disabled={isSaving}
                  className={`${btnSecondaryClass} disabled:opacity-50`}
                >
                  {archiveFeedback === 'draft' ? 'Gespeichert! ✓' : 'Später fortsetzen'}
                </button>
                <button
                  type="button"
                  onClick={goToPreview}
                  className={btnPrimaryClass}
                >
                  {!hasIban ? 'IBAN hinterlegen (für Vorschau)' : 'Weiter zur Vorschau'}
                </button>
              </>
            )}
          </div>
          {archiveFeedback === 'error' ? (
            <p className="px-6 pb-3 text-center text-[10px] text-red-400">
              Archivierung fehlgeschlagen. Bitte erneut versuchen.
            </p>
          ) : null}
        </div>
      </div>

      <PEInvoiceSettingsModal
        isOpen={isSettingsOpen}
        onClose={() => {
          setIsSettingsOpen(false);
          setBankHint(false);
        }}
        onSaved={() => {
          setIsSettingsOpen(false);
          setBankHint(false);
          const ibanOk = isValidIban(usePEInvoiceStore.getState().profile.iban || '');
          if (ibanOk) setCurrentStep('preview');
        }}
        requireBankForInvoice={bankHint || !hasIban}
      />
    </>
  );
};

export default PEInvoiceModal;
